import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Radio, Heart, CalendarClock, MessageSquareDot, Music4, Shield, LogIn, LogOut, User, Megaphone, Mic, HandHeart, BookOpen, Users, CalendarDays, CalendarCheck, Church, ExternalLink, Sunrise } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api, { API } from "@/lib/api";
import InstallButton from "@/components/InstallButton";
import ShareApp from "@/components/ShareApp";

const navItems = [
  { to: "/", label: "Listen", icon: Radio, testid: "nav-listen" },
  { to: "/live", label: "Gospel Show", icon: Mic, testid: "nav-live" },
  { to: "/favorites", label: "Favorites", icon: Heart, testid: "nav-favorites" },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule" },
  { to: "/events", label: "Events", icon: CalendarDays, testid: "nav-events" },
  { to: "/requests", label: "Requests", icon: Music4, testid: "nav-requests" },
  { to: "/bible", label: "Bible", icon: BookOpen, testid: "nav-bible" },
  { to: "/devotions", label: "Devotions", icon: Sunrise, testid: "nav-devotions" },
  { to: "/prayer-wall", label: "Prayer Wall", icon: Users, testid: "nav-prayer-wall" },
  { to: "/chat", label: "Live Chat", icon: MessageSquareDot, testid: "nav-chat" },
  { to: "/advertise", label: "Advertise", icon: Megaphone, testid: "nav-advertise" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { logo: brandLogo } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [liveNow, setLiveNow] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [savedBadge, setSavedBadge] = useState(0);
  useEffect(() => {
    const poll = () => api.get("/live/status").then((r) => setLiveNow(r.data?.active ? r.data : null)).catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!user) { setSavedBadge(0); return; }
    const poll = () => api.get("/saved-songs").then((r) => {
      const c = Array.isArray(r.data) ? r.data.length : 0;
      setSavedCount(c);
      const seen = parseInt(localStorage.getItem("savedSongsSeen") || "0", 10);
      setSavedBadge(Math.max(0, c - seen));
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [user]);
  useEffect(() => {
    if (location.pathname === "/favorites") {
      localStorage.setItem("savedSongsSeen", String(savedCount));
      setSavedBadge(0);
    }
  }, [location.pathname, savedCount]);
  const [callBadge, setCallBadge] = useState(0);
  useEffect(() => {
    if (!user || (user.role !== "host" && user.role !== "admin")) { setCallBadge(0); return; }
    const poll = () => api.get("/voice/callers").then((r) => {
      const list = Array.isArray(r.data?.callers) ? r.data.callers : [];
      setCallBadge(list.filter((c) => !c.on_air).length);
    }).catch(() => setCallBadge(0));
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [user]);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
      isActive ? "bg-[#E6B34A] text-[#0A1030]" : "text-zinc-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 border-r border-white/5 bg-[#0C1338] p-6">
      <div className="flex items-center gap-3 mb-6 shrink-0">
        <img src={brandLogo} alt="In His Image Radio" className="w-11 h-11 object-contain" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.3)) drop-shadow(0 0 10px rgba(230,179,74,0.4))" }} />
        <div>
          <p className="font-display font-extrabold text-lg tracking-tight leading-tight">In His Image</p>
          <p className="overline text-[#E6B34A] mt-1">Radio · Gospel</p>
        </div>
      </div>

      {liveNow && (
        <NavLink to="/live" data-testid="sidebar-onair" className="flex items-center gap-2 px-3 py-2 mb-4 rounded-xl bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF7A70] hover:bg-[#FF3B30]/25 transition-colors shrink-0">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
          </span>
          <span className="text-xs font-bold uppercase tracking-wide truncate">On air now</span>
          <Mic className="w-3.5 h-3.5 ml-auto shrink-0" />
        </NavLink>
      )}

      <nav className="flex flex-col gap-1.5 flex-1 overflow-y-auto min-h-0 -mr-2 pr-2">
        {navItems.map((item) => (
          <React.Fragment key={item.to}>
            <NavLink to={item.to} className={linkCls} data-testid={item.testid} end={item.to === "/"}>
              <item.icon className="w-5 h-5" /> {item.label}
              {item.to === "/favorites" && savedBadge > 0 && (
                <span data-testid="favorites-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold flex items-center justify-center">{savedBadge}</span>
              )}
            </NavLink>
            {item.to === "/bible" && user && (
              <NavLink to="/prayer" className={linkCls} data-testid="nav-prayer">
                <HandHeart className="w-5 h-5" /> Prayer Requests
              </NavLink>
            )}
          </React.Fragment>
        ))}
        {user && (
          <NavLink to="/my-events" className={linkCls} data-testid="nav-my-events">
            <CalendarCheck className="w-5 h-5" /> My Events
          </NavLink>
        )}
        {user && (
          <NavLink to="/my-spots" className={linkCls} data-testid="nav-myspots">
            <Megaphone className="w-5 h-5" /> My Spots
          </NavLink>
        )}
        {user && (user.role === "host" || user.role === "admin") && (
          <NavLink to="/host" className={linkCls} data-testid="nav-host">
            <Mic className="w-5 h-5" /> Host Studio
            {callBadge > 0 && <span data-testid="host-callin-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold flex items-center justify-center animate-pulse">{callBadge}</span>}
          </NavLink>
        )}
        {user && user.role === "admin" && (
          <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
            <Shield className="w-5 h-5" /> Admin
          </NavLink>
        )}
        <a
          href="https://ihimm.org"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-ministry"
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-zinc-200 bg-[#E6B34A]/10 border border-[#E6B34A]/30 hover:bg-[#E6B34A]/20 transition-colors mt-2"
        >
          <Church className="w-5 h-5 shrink-0 text-[#E6B34A]" />
          <span className="flex-1 leading-tight">
            In HIS Image Movement Ministries
            <span className="block text-[10px] font-bold uppercase tracking-wide text-[#E6B34A] mt-0.5">Parent Ministry</span>
          </span>
          <ExternalLink className="w-3.5 h-3.5 opacity-70" />
        </a>
      </nav>

      <div className="shrink-0 pt-4 mt-2 border-t border-white/5">
        <InstallButton className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 rounded-xl bg-[#E6B34A]/10 text-[#E6B34A] text-sm font-bold hover:bg-[#E6B34A]/20 transition-colors" />
        <ShareApp showCount className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-1 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors" />
        {user ? (
          <div className="space-y-3" data-testid="sidebar-user">
            <NavLink to="/profile" data-testid="nav-profile" className="flex items-center gap-3 rounded-xl hover:bg-white/5 -mx-2 px-2 py-1.5 transition-colors">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-[#5B7CFA]/20 border border-[#5B7CFA]/40 flex items-center justify-center shrink-0">
                {user.has_avatar
                  ? <img src={`${API}/users/${user.id}/avatar`} alt="" className="w-full h-full object-cover" data-testid="sidebar-avatar" />
                  : <User className="w-4 h-4 text-[#5B7CFA]" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{user.title ? `${user.title} ` : ""}{user.name}</p>
                <p className="text-xs text-zinc-500 truncate">View profile</p>
              </div>
            </NavLink>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        ) : (
          <p className="text-xs text-zinc-600 leading-relaxed" data-testid="sidebar-guest-hint">
            Sign in from the top-right to save favorites, join the chat and request songs.
          </p>
        )}
      </div>
    </aside>
  );
}
